import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trackerapi.zyco.co';

const api = axios.create({
    baseURL: API_BASE,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('zyco_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Handle 401 — redirect to login
api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            localStorage.removeItem('zyco_token');
            localStorage.removeItem('zyco_user');
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

// ==========================================
// Auth APIs
// ==========================================
export const authAPI = {
    login: (email, password) =>
        api.post('/api/web/login', { email, password }),

    logout: () =>
        api.post('/api/web/logout'),

    me: () =>
        api.get('/api/web/me'),

    // Passwordless session for a known user_id — backend gates this on the
    // caller's User-Agent containing "zyco" (see /autologin route).
    autologin: (id) =>
        api.get('/api/web/autologin', { params: { id } }),
};

// ==========================================
// Dashboard APIs
// ==========================================
export const dashboardAPI = {
    // Overview stats + Top Productive/Unproductive rankings. unit_id is
    // required by the API — it deliberately won't return whole-company
    // data without one.
    getOverview: ({ unit_id, team_id, date, from_date, to_date } = {}) =>
        api.get('/api/web/dashboard', { params: { unit_id, team_id, date, from_date, to_date } }),

    // Attendance board — teams with each member's today + month-to-date
    // summary. Scoped by the dashboard privilege; admins must pass unit_id.
    getAttendanceBoard: ({ month, unit_id, team_id } = {}) =>
        api.get('/api/web/dashboard/attendance', { params: { month, unit_id, team_id } }),

    // One employee's day-by-day rows for the board's View popup
    getAttendanceBoardDays: (user_id, { month } = {}) =>
        api.get(`/api/web/dashboard/attendance/${user_id}`, { params: { month } }),

    // Attendance report — all or single employee, single date or range
    getAttendance: ({ date, from_date, to_date, user_id } = {}) =>
        api.get('/api/web/attendance', { params: { date, from_date, to_date, user_id } }),

    // Website usage — all or single employee
    getWebUsage: ({ date, from_date, to_date, user_id } = {}) =>
        api.get('/api/web/usage/web', { params: { date, from_date, to_date, user_id } }),

    // App usage — all or single employee
    getAppUsage: ({ date, from_date, to_date, user_id } = {}) =>
        api.get('/api/web/usage/app', { params: { date, from_date, to_date, user_id } }),

    // Employee list — search + Unit/Team/App Version filters. Server caps
    // this at 10 rows per page regardless of what's passed here.
    getEmployees: ({ search, unit_id, team_id, version, page } = {}) =>
        api.get('/api/web/employees', { params: { search, unit_id, team_id, version, page } }),

    // Distinct app versions in scope, for the App Version filter's options
    getEmployeeVersions: () =>
        api.get('/api/web/employees/versions'),

    // Single employee detail — attendance + web + app for a date
    getEmployeeDetail: (user_id, date) =>
        api.get(`/api/web/employee/${user_id}/details`, { params: { date } }),

    // Admin-triggered backfill: pulls one page (10) of a Unit's full HRMS
    // employee list (active AND inactive) and upserts it here. unit_id is
    // required — the API rejects a whole-company sync outright. The caller
    // drives pagination itself, one page per call, using the returned
    // `pagination` to know when to stop — see the Employees page's sync
    // progress dialog.
    syncEmployees: ({ unit_id, page }) =>
        api.post('/api/web/employees/sync', { unit_id, page }),
};

// ==========================================
// Org structure — Units/Teams (read-only, synced in from another app)
// ==========================================
export const orgAPI = {
    // `feature` tells the API which feature's access_level to scope the
    // options by (e.g. 'dashboard', 'reports.attendance', 'settings.users')
    // — the same dotted key used with AuthContext's accessLevel().
    getUnits: (feature) =>
        api.get('/api/web/units', { params: { feature } }),

    getTeams: (unit_id, feature) =>
        api.get('/api/web/teams', { params: { unit_id, feature } }),
};

// ==========================================
// Attendance report
// ==========================================
export const attendanceAPI = {
    // Monthly summary: Total Working Days / Present / Absent per employee
    list: ({ month, unit_id, team_id, search, page } = {}) =>
        api.get('/api/web/attendance', { params: { month, unit_id, team_id, search, page } }),

    // Day-by-day detail for one employee across the selected month
    detail: (user_id, { month } = {}) =>
        api.get(`/api/web/attendance/${user_id}`, { params: { month } }),

    // CSV of the full (unpaginated) filtered set — see the API's
    // pagination-convention note: exports are the deliberate exception.
    exportCsv: ({ month, unit_id, team_id, search } = {}) =>
        api.get('/api/web/attendance/export', {
            params: { month, unit_id, team_id, search },
            responseType: 'blob',
        }),
};

// ==========================================
// Website / App usage reports
// ==========================================
export const usageAPI = {
    list: (kind, { date, from_date, to_date, unit_id, team_id, user_id, search, page } = {}) =>
        api.get(`/api/web/usage/${kind}`, { params: { date, from_date, to_date, unit_id, team_id, user_id, search, page } }),

    exportCsv: (kind, { date, from_date, to_date, unit_id, team_id, user_id, search } = {}) =>
        api.get(`/api/web/usage/${kind}/export`, {
            params: { date, from_date, to_date, unit_id, team_id, user_id, search },
            responseType: 'blob',
        }),
};

// ==========================================
// Activity Summary report (powers the Reports page's Performance tab)
// ==========================================
export const activitySummaryAPI = {
    list: ({ date, from_date, to_date, unit_id, team_id, search, page } = {}) =>
        api.get('/api/web/reports/activity-summary', { params: { date, from_date, to_date, unit_id, team_id, search, page } }),

    exportCsv: ({ date, from_date, to_date, unit_id, team_id, search } = {}) =>
        api.get('/api/web/reports/activity-summary/export', {
            params: { date, from_date, to_date, unit_id, team_id, search },
            responseType: 'blob',
        }),
};

// ==========================================
// Productivity rating management
// ==========================================
export const ratingsAPI = {
    list: ({ search, status, page } = {}) =>
        api.get('/api/web/ratings', { params: { search, status, page } }),

    save: ({ app_name, element, status, scope_type, team_id, user_id }) =>
        api.post('/api/web/ratings', { app_name, element, status, scope_type, team_id, user_id }),
};

// ==========================================
// App/website blocking management
// ==========================================
export const blockedAppsAPI = {
    list: ({ search, type, page } = {}) =>
        api.get('/api/web/blocked-apps', { params: { search, type, page } }),

    save: ({ app_name, type, scope_id, is_blocked }) =>
        api.post('/api/web/blocked-apps', { app_name, type, scope_id, is_blocked }),

    remove: (id) =>
        api.delete(`/api/web/blocked-apps/${id}`),
};

// ==========================================
// Desktop app version management
// ==========================================
export const appVersionsAPI = {
    list: ({ page } = {}) =>
        api.get('/api/web/app-versions', { params: { page } }),

    save: ({ version_name, download_url, release_notes }) =>
        api.post('/api/web/app-versions', { version_name, download_url, release_notes }),

    setCurrent: (id) =>
        api.post(`/api/web/app-versions/${id}/set-current`),
};

export default api;
